import type { CalAttendee, CalEvent, GraphEventDto } from "../types";
import { dayStart, nearestLocalDay, parseGraphUtc } from "./time";

function mapResponse(raw: string | undefined): CalAttendee["response"] {
  if (raw === "accepted") return "yes";
  if (raw === "tentativelyAccepted") return "may";
  if (raw === "declined") return "no";
  return "wait";
}

/** When someone is on the invite twice, the stronger listing wins: a person over a room, required over optional. */
const TYPE_RANK: Record<CalAttendee["type"], number> = { required: 0, optional: 1, resource: 2 };

/**
 * The invite's attendees, each email address once. An invite can list the
 * same person twice (e.g. on both the Required and Optional lines); they are
 * merged so they show, count and get updated as one person.
 */
function mapAttendees(dto: GraphEventDto): CalAttendee[] {
  const byEmail = new Map<string, CalAttendee>();
  for (const a of dto.attendees ?? []) {
    const email = (a.emailAddress?.address ?? "").trim().toLowerCase();
    if (!email) continue;
    const next: CalAttendee = {
      name: (a.emailAddress?.name ?? "").trim() || email,
      email,
      response: mapResponse(a.status?.response),
      type: a.type === "optional" || a.type === "resource" ? a.type : "required",
    };
    const seen = byEmail.get(email);
    if (!seen) {
      byEmail.set(email, next);
      continue;
    }
    byEmail.set(email, {
      ...seen,
      type: TYPE_RANK[next.type] < TYPE_RANK[seen.type] ? next.type : seen.type,
      response: seen.response === "wait" ? next.response : seen.response,
    });
  }
  return [...byEmail.values()];
}

/** Graph calendarView item → CalEvent, or null when it has no id or unreadable times. */
export function mapEvent(dto: GraphEventDto): CalEvent | null {
  if (!dto.id) return null;
  const rawStart = parseGraphUtc(dto.start?.dateTime);
  const rawEnd = parseGraphUtc(dto.end?.dateTime);
  if (!rawStart || !rawEnd || rawEnd < rawStart) return null;

  const isAllDay = dto.isAllDay === true;
  const isOrganizer = dto.isOrganizer === true;
  const joinUrl = dto.onlineMeeting?.joinUrl ?? "";
  const organizer = dto.organizer?.emailAddress;
  const attendees = mapAttendees(dto);

  return {
    id: dto.id,
    subject: (dto.subject ?? "").trim(),
    start: isAllDay ? dayStart(nearestLocalDay(rawStart)) : rawStart,
    end: isAllDay ? dayStart(nearestLocalDay(rawEnd)) : rawEnd,
    isAllDay,
    location: (dto.location?.displayName ?? "").trim(),
    organizerName: (organizer?.name || organizer?.address || "").trim(),
    organizerEmail: (organizer?.address ?? "").trim().toLowerCase(),
    isOrganizer,
    response: isOrganizer ? "organizer" : mapResponse(dto.responseStatus?.response),
    responseRequested: dto.responseRequested !== false,
    showAs: dto.showAs ?? "busy",
    isCancelled: dto.isCancelled === true,
    attendeeCount: attendees.filter((a) => a.type !== "resource").length,
    attendees,
    joinUrl: joinUrl.startsWith("https://") ? joinUrl : null,
    webLink: dto.webLink ?? "",
    isRecurring: dto.type === "occurrence" || dto.type === "exception",
    seriesMasterId: dto.seriesMasterId ?? null,
    hasAttachments: dto.hasAttachments === true,
    isDraft: dto.isDraft === true,
    allowNewTimeProposals: dto.allowNewTimeProposals !== false,
    bodyPreview: (dto.bodyPreview ?? "").trim(),
    changeKey: dto.changeKey ?? "",
  };
}
